import React, {useCallback, useEffect, useState} from "react";
import {Paper} from "@mui/material";
import {Col, Row} from "reactstrap";
import Api from "../../../../../../core/services/api/api";
import useIsMounted from "../../../../../hooks/use-is-mounted";
import {ReactComponent as Info} from "../../../../../../assets/images/info.svg";
import * as Yup from "yup";
import ValidateMessages from "../../../../../../core/models/static/texts/vallidate-messages";
import {makeRequired, makeValidate} from "mui-rff";
import Form from "../../../../../components/base/form";
import {OutlinedInputForm} from "../../../../../components/base/input/outlined_input";
import classnames from "classnames";
import Button from "../../../../../components/base/button/button";
import {useConfigureNotebook} from "../../../../../hooks/use-notebook-config";
import DateUtils from "../../../../../../core/services/date-utils";
import useBehavioralAnalyticsTool from "../../../../../hooks/use-behavioural-analytics-tool";
import {useOperatorInformation} from "../../../../../hooks/use-operator-information";

const SalesPersonDashboard = () => {
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState(null);
    const isMounted = useIsMounted();
    const {configureNotebookTags} = useConfigureNotebook('sales-person-dashboard-view');
    const {configureBehavioralTool} = useBehavioralAnalyticsTool();

    /**
     * As soon as the component is mounted:
     * - Configures the notebook tags for the view
     * - Configures the behavioral analytics tool for the view
     */
    useEffect(() => {
        configureNotebookTags((noteTags) => noteTags.salesPersonDashboard);
        configureBehavioralTool((tags) => tags.salesPersonDashboard);
    }, [configureBehavioralTool, configureNotebookTags]);

    /**
     * Fetches the sales-person profile information of the operator
     */
    const getProfileInfo = useCallback(async () => {
        setLoading(true);
        const response = await Api.getSalesPersonProfile();
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setData(response.data);
        }
        setLoading(false);
    }, [isMounted])

    /**
     * As soon as the component mounts:
     * fetches the information of the sales person from the server.
     */
    useEffect(() => {
        void getProfileInfo();
    }, [getProfileInfo])

    return (
        <>
            <Paper className='paper flex-wrap background-white p-3 py-4 sales-person-dashboard'>
                {
                    loading
                        ? <>
                            <Row className={'w-100 mt-2'}>
                                <Col className={'mx-2 mx-md-4 mx-lg-5 loading sales-person-info'}>
                                    <div/>
                                </Col>
                            </Row>
                            <Row className={'mt-4 mb-5 w-100'}>
                                <Col className={'loading sales-person-info mini'}>
                                    <div/>
                                </Col>
                                <Col className={'loading sales-person-info mini'}>
                                    <div/>
                                </Col>
                                <Col className={'loading sales-person-info mini'}>
                                    <div/>
                                </Col>
                            </Row>
                        </>
                        : !data
                            ? <RegisterSalesPerson setData={setData}/>
                            : <>
                                <Row className={'w-100 mb-5 mt-2'}>
                                    <Col className={'mx-2 mx-md-4 mx-lg-5'}>
                                        <div className={'info-card'}>
                                            <p className={'title'}>
                                                {data?.name ?? ''}
                                            </p>
                                            <p className={''}>
                                                Your Salesperson No (code):
                                                <span>{data?.code ?? ''}</span>
                                            </p>
                                            <p className={''}>
                                                joined:
                                                <span>
                                                {data?.submittedDateTime ? DateUtils.serverToLocal(data.submittedDateTime).format(DateUtils.dateFormat) : ''}
                                            </span>
                                            </p>
                                        </div>
                                    </Col>
                                </Row>
                                <Row className={'mt-2 mb-5 w-100'}>
                                    <Col className={'ml-2 ml-md-4 ml-lg-5 order-card-container'}>
                                        <div className={'order-card-container'}>
                                            <div className={'order-card quotes'}>
                                                <p>
                                                    {data?.quoteCount ?? ''}
                                                </p>
                                            </div>
                                            <p>Submitted Online Quotes</p>
                                        </div>
                                    </Col>
                                    <Col className={'ml-2 ml-md-4 ml-lg-5 order-card-container'}>
                                        <div className={'order-card-container'}>
                                            <div className={'order-card sales-orders'}>
                                                <p>
                                                    {data?.salesOrderCount ?? ''}
                                                </p>
                                            </div>
                                            <p>Submitted Online Sales Orders</p>
                                        </div>
                                    </Col>
                                    <Col className={'ml-2 ml-md-4 ml-lg-5 order-card-container'}>
                                        <div className={'order-card-container'}>
                                            <div className={'order-card invoices'}>
                                                <p>
                                                    {data?.invoiceCount ?? ''}
                                                </p>
                                            </div>
                                            <p>Submitted Online Invoices</p>
                                        </div>
                                    </Col>
                                </Row>
                            </>
                }
            </Paper>

        </>
    )
}

const schema = Yup.object().shape({
    name: Yup.string().nullable().required(ValidateMessages.required),
    code: Yup.string().nullable().max(8,
        <span className={'d-flex align-items-center'}
              style={{marginLeft: -8, marginTop: 3}}>
            <Info
                className={classnames('red')}/>
            <span className={classnames('ml-2', 'red')}>
                Maximum 8 Characters
            </span>
        </span>
    ).required(ValidateMessages.required),
});


const RegisterSalesPerson = ({setData}) => {
    const isMounted = useIsMounted();
    const [registering, setRegistering] = useState(false);
    const validate = makeValidate(schema);
    const required = makeRequired(schema);
    const operatorInformation = useOperatorInformation();
    
    /**
     * Creates a new sales person profile for the current operator with the given values.
     * @param {any} values
     */
    const onSubmit = (values) => {
        const forApi = {
            ...values,
        }
        setRegistering(true);
        Api.createSalesPersonProfile(forApi).then((response) => {
            if (!isMounted()) return;
            if (response?.resultFlag) {
                setData(response.data);
            }
            setRegistering(false);
        })
    }

    return (
        <>
            <Row className={'title p-3'}>
                <p>
                    Sales Person Profile
                </p>
            </Row>
            <Row className={'description px-3 pb-3'}>
                <p>
                    You do not have a salesperson profile. please complete the following form for activating your
                    profile
                </p>
            </Row>
            <Form
                className='w-100 register-form mt-3'
                onSubmit={onSubmit}
                validate={validate}
                initialValues={{name: operatorInformation?.name ?? ""}}
                render={({submitting, values, form}) => {
                    const colClass = 'px-3 mb-4';
                    return (
                        <>
                            <Row className='no-gutters justify-content-around w-100'>
                                <Col xs={12} className={colClass}>
                                    <OutlinedInputForm
                                        label={'Name'}
                                        name={'name'}
                                        type={'text'}
                                        placeholder={'Enter your sales person name'}
                                        required={required?.name}
                                        value={values?.name ?? ''}
                                        onChange={(e) => form.change(e.target.name, e.target.value)}
                                    />
                                </Col>
                                <Col xs={12} className={colClass}>
                                    <OutlinedInputForm
                                        label={'Code'}
                                        name={'code'}
                                        type={'text'}
                                        required={required?.code}
                                        value={values?.code ?? ''}
                                        onChange={(e) => form.change(e.target.name, e.target.value)}
                                        placeholder={"maximum 8 characters"}
                                        helperText={
                                            <span className={'d-flex align-items-center'} style={{marginLeft: -12}}>
                                                <Info
                                                    className={classnames({'green': (values?.code?.length ?? 0) < 9}, {'red': values?.code?.length > 8})}/>
                                                <span
                                                    className={classnames('ml-2', {'green': (values?.code?.length ?? 0) < 9}, {'red': values?.code?.length > 8})}>
                                                    Maximum 8 Characters
                                                </span>
                                            </span>
                                        }
                                    />
                                </Col>
                                <Col xs={12} className={classnames(colClass, 'd-flex justify-content-end')}>
                                    <Button
                                        disabled={submitting}
                                        className='py-2-5'
                                        type={'submit'}
                                        variant={'contained'}
                                        color={'primary'}
                                    >
                                        {!registering ? 'Create Profile' : 'processing...'}
                                    </Button>
                                </Col>
                            </Row>
                        </>
                    );
                }}
            />
        </>
    );
}

export default SalesPersonDashboard;
